import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Workflow, ArrowRight, Clock, AlertCircle, Eye, Plus, Trash2 } from 'lucide-react';

/**
 * Sub-Workflow Configuration Component
 * 
 * Simple UI for calling another workflow as a step.
 * Designed to be understandable by a 10-year-old.
 */
const SubWorkflowConfig = ({ config, onChange }) => {
  const [workflowId, setWorkflowId] = useState(config?.workflowId || '');
  const [inputs, setInputs] = useState(config?.inputs || {});
  const [outputVariable, setOutputVariable] = useState(config?.outputVariable || 'subworkflow_output');
  const [timeout, setTimeout] = useState(config?.timeout || 300);
  const [onError, setOnError] = useState(config?.onError || 'stop');

  const updateConfig = (updates) => {
    onChange({
      ...config,
      ...updates
    });
  };

  const addInput = () => {
    const newInputs = { ...inputs, [`input_${Object.keys(inputs).length + 1}`]: '' };
    setInputs(newInputs);
    updateConfig({ inputs: newInputs });
  };

  const removeInput = (key) => {
    const newInputs = { ...inputs };
    delete newInputs[key];
    setInputs(newInputs);
    updateConfig({ inputs: newInputs });
  };

  const updateInput = (key, value) => {
    const newInputs = { ...inputs, [key]: value };
    setInputs(newInputs);
    updateConfig({ inputs: newInputs });
  };

  const updateInputKey = (oldKey, newKey) => {
    const newInputs = {};
    Object.keys(inputs).forEach(k => {
      if (k === oldKey) {
        newInputs[newKey] = inputs[k];
      } else {
        newInputs[k] = inputs[k];
      }
    });
    setInputs(newInputs);
    updateConfig({ inputs: newInputs });
  };

  const errorHandlingOptions = [
    { value: 'stop', label: 'Stop Everything', description: 'Stop the whole workflow if this fails' },
    { value: 'continue', label: 'Keep Going', description: 'Continue even if this fails' },
    { value: 'retry', label: 'Try Again', description: 'Retry a few times before giving up' }
  ];

  // Mock workflow list (in production, fetch from API)
  const availableWorkflows = [
    { id: 'wf_process_payment', name: 'Process Payment' },
    { id: 'wf_send_email', name: 'Send Email' },
    { id: 'wf_validate_order', name: 'Validate Order' },
    { id: 'wf_ship_order', name: 'Ship Order' }
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Workflow className="w-5 h-5 text-purple-500" />
          <h3 className="text-lg font-semibold">Run Another Workflow</h3>
        </div>
        <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-500/30">
          Sub-Workflow
        </Badge>
      </div>

      {/* Explanation */}
      <Card className="bg-purple-500/5 border-purple-500/20">
        <CardContent className="pt-4">
          <p className="text-sm text-muted-foreground">
            <strong>What does this do?</strong> This runs another workflow as a step in this one. 
            Think of it like calling a friend to help you with part of a task.
          </p>
        </CardContent>
      </Card>

      {/* Workflow Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Which workflow do you want to run?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={workflowId} onValueChange={(value) => {
            setWorkflowId(value);
            updateConfig({ workflowId: value });
          }}>
            <SelectTrigger>
              <SelectValue placeholder="Select a workflow..." />
            </SelectTrigger>
            <SelectContent>
              {availableWorkflows.map((wf) => (
                <SelectItem key={wf.id} value={wf.id}>
                  <div className="flex items-center gap-2">
                    <Workflow className="w-4 h-4" />
                    <span>{wf.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {workflowId && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs">
                <strong>Selected:</strong> {availableWorkflows.find(w => w.id === workflowId)?.name}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Input Mapping */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">What data should it receive?</CardTitle>
            <Button size="sm" variant="outline" onClick={addInput}>
              <Plus className="w-4 h-4 mr-1" />
              Add Input
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {Object.keys(inputs).length === 0 ? (
            <div className="p-4 border-2 border-dashed rounded-lg text-center text-muted-foreground">
              <p className="text-sm">No inputs yet. Click "Add Input" to send data to the workflow.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(inputs).map(([key, value]) => (
                <div key={key} className="flex gap-2 items-start">
                  <div className="flex-1 space-y-2">
                    <Input
                      placeholder="Input name (e.g., order_id)"
                      value={key}
                      onChange={(e) => updateInputKey(key, e.target.value)}
                      className="font-mono text-sm"
                    />
                    <div className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Value (e.g., {{trigger.order_id}})"
                        value={value}
                        onChange={(e) => updateInput(key, e.target.value)}
                        className="font-mono text-sm flex-1"
                      />
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeInput(key)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Map data from this workflow to the sub-workflow. Use <code className="bg-muted px-1 rounded">{`{{variable}}`}</code> to pass values.
          </p>
        </CardContent>
      </Card>

      {/* Output Variable */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Where should the result be saved?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input
            placeholder="e.g., payment_result, order_status"
            value={outputVariable}
            onChange={(e) => {
              setOutputVariable(e.target.value);
              updateConfig({ outputVariable: e.target.value });
            }}
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            After the sub-workflow finishes, you can use its result with {`{{${outputVariable}}}`}
          </p>
        </CardContent>
      </Card>

      {/* Timeout */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-orange-500" />
            <CardTitle className="text-sm font-medium">How long should it wait?</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Timeout: {timeout} seconds</Label>
              <Badge variant="outline">{Math.floor(timeout / 60)} min {timeout % 60} sec</Badge>
            </div>
            <Slider
              value={[timeout]}
              onValueChange={([value]) => {
                setTimeout(value);
                updateConfig({ timeout: value });
              }}
              min={30}
              max={600}
              step={30}
              className="w-full"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            If the sub-workflow takes longer than this, it will be cancelled.
          </p>
        </CardContent>
      </Card>

      {/* Error Handling */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <CardTitle className="text-sm font-medium">What if it fails?</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={onError} onValueChange={(value) => {
            setOnError(value);
            updateConfig({ onError: value });
          }}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {errorHandlingOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex flex-col">
                    <span className="font-medium">{option.label}</span>
                    <span className="text-xs text-muted-foreground">{option.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-xs">
              <strong>Selected:</strong> {errorHandlingOptions.find(o => o.value === onError)?.label}
              <br />
              {errorHandlingOptions.find(o => o.value === onError)?.description}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="bg-purple-500/5 border-purple-500/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            <span className="text-muted-foreground">Run workflow:</span>{' '}
            <span className="font-semibold">{availableWorkflows.find(w => w.id === workflowId)?.name || '(not selected)'}</span>
          </p>
          <p>
            <span className="text-muted-foreground">With inputs:</span>{' '}
            <Badge variant="outline">{Object.keys(inputs).length} inputs</Badge>
          </p>
          <p>
            <span className="text-muted-foreground">Save result as:</span>{' '}
            <span className="font-mono text-xs">{`{{${outputVariable}}}`}</span>
          </p>
          <p>
            <span className="text-muted-foreground">Timeout:</span>{' '}
            <span className="text-xs">{timeout}s</span>
          </p>
          <p>
            <span className="text-muted-foreground">On error:</span>{' '}
            <Badge variant="outline" className="bg-red-500/10 text-red-600">
              {errorHandlingOptions.find(o => o.value === onError)?.label}
            </Badge>
          </p>
        </CardContent>
      </Card>

      {/* Example */}
      <Card className="bg-muted/50">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <CardTitle className="text-sm font-medium">Example</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-xs">
            <p className="text-muted-foreground">
              <strong>Scenario:</strong> You want to process a payment as part of an order workflow.
            </p>
            <div className="p-2 bg-background rounded border space-y-1">
              <p><strong>Workflow:</strong> Process Payment</p>
              <p><strong>Inputs:</strong></p>
              <ul className="list-disc list-inside ml-2">
                <li>amount → {`{{trigger.total}}`}</li>
                <li>card → {`{{trigger.payment_method}}`}</li>
              </ul>
              <p><strong>Output:</strong> payment_result</p>
              <p><strong>On Error:</strong> Stop Everything</p>
            </div>
            <p className="text-muted-foreground">
              Now you can check {`{{payment_result.status}}`} in later steps!
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubWorkflowConfig;
